import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiCheck,
  FiCopy,
  FiMail,
  FiTruck,
  FiMapPin,
  FiArrowRight,
} from "react-icons/fi";
import { BsCashCoin } from "react-icons/bs";
import styled, { css, keyframes } from "styled-components";

import MainContainer from "../../../components/utils/MainContainer";
import { formatShipping } from "../CartPage/helpers";
import { formatPrice } from "../../../utils/utils";
import { toast } from "../../../components/modern-ui/sonner";
import {
  COUNTRY_CONFIG,
  DEFAULT_COUNTRY,
  PICKUP_INFO,
} from "../../../utils/constants";

/*
 * Potvrda porudžbine, v2 s.14.
 * Refresh ne duplira porudžbinu (samo čita state); direktan ulazak bez
 * porudžbine → redirect; korpa je već ispražnjena tek POSLE uspešnog
 * kreiranja (u CartPage).
 */

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const orderData = location.state?.orderData;

  const {
    orderNumber = "",
    total = 0,
    itemsList = [],
    customerInfo = {},
    deliveryMethod = "delivery",
    country = DEFAULT_COUNTRY,
    countryName = "",
    shipping = 0,
  } = orderData || {};

  const isPickup = deliveryMethod === "pickup";
  const countryConfig =
    COUNTRY_CONFIG[country] || COUNTRY_CONFIG[DEFAULT_COUNTRY];

  const {
    firstName = "",
    lastName = "",
    email = "",
    address = "",
    city = "",
    zipCode = "",
  } = customerInfo;

  useEffect(() => {
    window.scrollTo(0, 0);

    // Direktan ulazak bez porudžbine → redirect na prodavnicu (v2 s.14.2)
    if (!orderData) {
      const timeoutId = setTimeout(() => {
        navigate("/prodavnica");
      }, 2500);
      return () => clearTimeout(timeoutId);
    }
  }, [orderData, navigate]);

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const copyOrderNumber = () => {
    navigator.clipboard.writeText(orderNumber);
    toast.success("Broj porudžbine kopiran");
  };

  if (!orderData) {
    return (
      <MainContainer>
        <Wrap>
          <StateNote>
            Nema podataka o porudžbini, vraćamo te u prodavnicu...
          </StateNote>
        </Wrap>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Wrap>
        {/* 1. Potvrda */}
        <ConfirmHead>
          <CheckCircle>
            <FiCheck size={28} />
          </CheckCircle>
          <h1>Porudžbina je primljena</h1>
          <OrderNumber>
            <strong>{orderNumber}</strong>
            <button
              type="button"
              onClick={copyOrderNumber}
              title="Kopiraj broj porudžbine"
              aria-label="Kopiraj broj porudžbine"
            >
              <FiCopy size={16} />
            </button>
          </OrderNumber>
        </ConfirmHead>

        {/* 2. Šta dalje */}
        <Card>
          <CardLabel>Šta dalje</CardLabel>
          <StepsList>
            <li>
              <FiMail size={16} />
              <span>
                Potvrda je poslata na <strong>{email}</strong>
              </span>
            </li>
            <li>
              <FiTruck size={16} />
              <span>
                {isPickup
                  ? `Javićemo ti se čim paket bude spreman za preuzimanje u ${PICKUP_INFO.cityLocative}.`
                  : `Pakujemo i šaljemo PostExpresom, isporuka ${countryConfig.deliveryEstimate}.`}
              </span>
            </li>
            <li>
              <BsCashCoin size={16} />
              <span>
                Plaćaš pouzećem: pripremi <strong>{formatPrice(total)}</strong>
                {!isPickup && shipping > 0 ? " i poštarinu po cenovniku Pošte" : ""}{" "}
                {isPickup ? "prilikom preuzimanja" : "za kurira"}.
              </span>
            </li>
          </StepsList>
        </Card>

        {/* 3. Rekapitulacija */}
        <Card>
          <CardLabel>Rekapitulacija porudžbine</CardLabel>

          {itemsList.length > 0 && (
            <ItemsList>
              {itemsList.map((item) => (
                <li key={item.cartItemId}>
                  {item.mainImage && (
                    <img src={item.mainImage} alt="" loading="lazy" />
                  )}
                  <div className="info">
                    <span className="name">{item.name}</span>
                    <span className="meta">
                      {item.selectedSize ? `Vel. ${item.selectedSize} · ` : ""}
                      {item.quantity} kom
                    </span>
                  </div>
                  <span className="price">
                    {formatPrice(
                      (item.salePrice || item.price) * item.quantity,
                    )}
                  </span>
                </li>
              ))}
            </ItemsList>
          )}

          <SummaryRows>
            <div>
              <span>Poštarina</span>
              <span>
                {isPickup
                  ? "Lično preuzimanje"
                  : shipping === 0
                    ? "Besplatna"
                    : formatShipping(shipping, country)}
              </span>
            </div>
            <div>
              <span>Plaćanje</span>
              <span>Pouzećem</span>
            </div>
            <div className="total">
              <span>{!isPickup && shipping > 0 ? "Ukupno bez poštarine" : "Ukupno"}</span>
              <span>{formatPrice(total)}</span>
            </div>
          </SummaryRows>

          <AddressBlock>
            <FiMapPin size={15} />
            <div>
              {isPickup ? (
                <>
                  <strong>Preuzimanje: {PICKUP_INFO.city}</strong>
                  <span>{PICKUP_INFO.address}</span>
                  <span>Radno vreme: {PICKUP_INFO.hours}</span>
                </>
              ) : (
                <>
                  <strong>
                    {firstName} {lastName}
                  </strong>
                  <span>{address}</span>
                  <span>
                    {zipCode} {city}
                    {countryName ? `, ${countryName}` : ""}
                  </span>
                </>
              )}
            </div>
          </AddressBlock>

          <SupportNote>
            Pogrešni podaci? Javi nam se na{" "}
            <a href="mailto:info@mrc.rs">info@mrc.rs</a>
            {/* ili {SELLER_INFO.phone}{" "} */} uz broj porudžbine.
          </SupportNote>
        </Card>

        {/* 4. CTA red */}
        <CtaRow>
          <PrimaryBtn type="button" onClick={() => goTo("/prodavnica?page=1")}>
            Nastavi kupovinu
          </PrimaryBtn>
          <GhostBtn type="button" onClick={() => goTo("/bendovi?page=1")}>
            Pogledaj bendove <FiArrowRight size={15} />
          </GhostBtn>
        </CtaRow>
      </Wrap>
    </MainContainer>
  );
};

export default OrderSuccessPage;

/* ------------------------------- stilovi ------------------------------- */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const checkPop = keyframes`
  0% { opacity: 0; transform: scale(0.35); }
  60% { transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
`;

const ringOut = keyframes`
  from { box-shadow: 0 0 0 0 rgba(61, 220, 116, 0.4); }
  to { box-shadow: 0 0 0 20px rgba(61, 220, 116, 0); }
`;

const Wrap = styled.div`
  max-width: 640px;
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 5rem;

  /* blokovi potvrde ulaze jedan za drugim */
  & > * {
    animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 5 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${i * 130}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

const StateNote = styled.p`
  text-align: center;
  padding: 5rem 1rem;
  color: var(--text-secondary);
`;

const ConfirmHead = styled.div`
  text-align: center;
  margin: 1.5rem 0 2rem;

  h1 {
    margin: 1.25rem 0 0.75rem;
    font-family: var(--font-display);
    font-size: clamp(26px, 4vw, 40px);
    line-height: 1;
    letter-spacing: -0.01em;
    text-transform: uppercase;
    color: var(--text-primary);
  }
`;

const CheckCircle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(61, 220, 116, 0.14);
  border: 2px solid var(--success);
  color: var(--success);
  /* check iskoči sa overshoot-om, pa talas zelenog prstena */
  animation:
    ${checkPop} 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s backwards,
    ${ringOut} 0.9s ease-out 0.6s backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const OrderNumber = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-family: var(--font-display);
    font-size: 1.5rem;
    color: var(--brand);
    letter-spacing: 0.01em;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-control);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
      color: var(--brand);
      border-color: var(--brand);
    }
  }
`;

const Card = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.5rem;
  margin-bottom: 1rem;
  text-align: left;
`;

const CardLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1rem;
`;

const StepsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    color: var(--text-secondary);
    font-size: 0.95rem;
    line-height: 1.5;

    svg {
      color: var(--brand);
      flex-shrink: 0;
      margin-top: 0.2rem;
    }

    strong {
      color: var(--text-primary);
    }
  }
`;

const ItemsList = styled.ul`
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.55rem 0;
    border-bottom: 1px solid var(--border);

    img {
      width: 44px;
      height: 44px;
      object-fit: cover;
      border-radius: var(--radius-control);
      border: 1px solid var(--border);
      background: var(--bg-elevated);
      flex-shrink: 0;
    }

    .info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .name {
      color: var(--text-primary);
      font-size: 0.9rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .meta {
      color: var(--text-secondary);
      font-size: 13px;
    }

    .price {
      color: var(--text-primary);
      font-size: 0.9rem;
      font-weight: 700;
      white-space: nowrap;
    }
  }
`;

const SummaryRows = styled.div`
  div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    padding: 0.45rem 0;
    color: var(--text-secondary);
    font-size: 0.9rem;

    span:last-child {
      color: var(--text-primary);
      font-weight: 600;
    }

    &.total {
      border-top: 1px solid var(--border-strong);
      margin-top: 0.5rem;
      padding-top: 0.75rem;

      span:first-child {
        color: var(--text-primary);
        font-weight: 700;
      }

      span:last-child {
        font-size: 1.2rem;
        font-weight: 700;
      }
    }
  }
`;

const AddressBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 1.25rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-control);

  svg {
    color: var(--brand);
    flex-shrink: 0;
    margin-top: 0.2rem;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    color: var(--text-secondary);
    font-size: 0.9rem;

    strong {
      color: var(--text-primary);
    }
  }
`;

const SupportNote = styled.p`
  margin: 1rem 0 0;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.5;

  a {
    color: var(--brand);
  }
`;

const CtaRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const PrimaryBtn = styled.button`
  flex: 1;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: var(--brand-hover);
  }
`;

const GhostBtn = styled.button`
  flex: 1;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;
